(() => {
  if (window.CareizCart) return;
  const node = document.querySelector('[data-careiz-cart-policy]');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const root = `${config.root.replace(/\/$/,'')}/`;
  const upper = value => String(value || '').trim().toUpperCase();
  const internal = code => upper(code).startsWith('CAREIZ-ADD-');
  const unique = values => [...new Map(values.filter(Boolean).map(value => [upper(value),String(value).trim()])).values()];
  const same = (left,right) => left.map(upper).sort().join(',') === right.map(upper).sort().join(',');

  class CartPolicy {
    constructor() { this.queue = Promise.resolve();this.message = '';this.currentCode = '';this.allowedForms = new WeakSet(); }
    async get() {
      const response = await fetch(`${root}cart.js`,{headers:{Accept:'application/json'},cache:'no-store'});
      if (!response.ok) throw new Error(config.labels.error);
      return response.json();
    }
    sections() {
      const ids = ['cart-drawer','cart-icon-bubble'];
      for (const id of ['main-cart-items','main-cart-footer']) {
        const host = document.getElementById(id);
        if (host) ids.push(host.dataset.id || id);
      }
      return {sections:unique(ids).slice(0,5),sections_url:location.pathname};
    }
    async update(body) {
      const {updates,...remaining} = body;
      let cart;
      if (updates && Object.keys(updates).length) cart = await this.send({updates});
      if (!cart || Object.keys(remaining).length) cart = await this.send(remaining);
      return cart;
    }
    async send(body) {
      const response = await fetch(`${root}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({...body,...this.sections()})});
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status) throw new Error(typeof cart.description === 'string' ? cart.description : config.labels.error);
      return cart;
    }
    allocations(cart) {
      return [...(cart.cart_level_discount_applications || []),...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation=>allocation.discount_application))].filter(Boolean);
    }
    codes(cart) {
      let retained = [];
      try { const parsed = JSON.parse(cart.attributes?._careiz_applied_codes || '[]');if (Array.isArray(parsed)) retained = parsed.filter(value=>typeof value === 'string'); } catch {}
      return unique([...(cart.discount_codes || []).map(discount=>discount.code),...this.allocations(cart).filter(discount=>discount.type === 'discount_code').map(discount=>discount.code || discount.title),...retained,cart.attributes?._careiz_marketing_code]);
    }
    marketing(cart) {
      const candidates = this.codes(cart).filter(code=>!internal(code));
      const applied = this.allocations(cart).filter(discount=>discount.type === 'discount_code').map(discount=>discount.code || discount.title).find(code=>!internal(code));
      return candidates.find(code=>upper(code) === upper(applied)) || candidates[0] || '';
    }
    normalize(options = {}) {
      const task = this.queue.then(()=>this.perform(options));
      this.queue = task.catch(()=>{});
      return task;
    }
    removeLine(index) {
      const task = this.queue.then(async ()=> {
        const before = await this.get();
        const item = before.items[Number(index)-1];
        if (!item) return;
        const changed = await this.update({updates:{[item.key]:0}});
        const result = await this.perform({cart:changed});
        this.render(result.cart);this.paint();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'careiz-cart-policy',cartData:result.cart});
      });
      this.queue = task.catch(()=>{});
      return task;
    }
    expected(item,cart) {
      const amount = item.properties?._careiz_expected_price;
      if (amount !== undefined && item.properties?._careiz_currency === cart.currency && Number.isFinite(Number(amount))) return Number(amount);
      for (const node of document.querySelectorAll('[data-careiz-config]')) {
        try {
          const settings = JSON.parse(node.textContent);
          if (settings.currency !== cart.currency) continue;
          const offer = settings.rules.find(rule=>Number(rule.productId) === Number(item.product_id) && (!Number(rule.triggerId) || Number(rule.triggerId) === Number(item.properties?._careiz_main_product)) && upper(rule.discount?.code) === upper(item.properties?._careiz_discount_code));
          if (!offer) continue;
          const price = Number(item.original_price ?? item.price);
          if (offer.discount.type === 'percentage') return Math.max(0,Math.round(price*(1-Number(offer.discount.value)/100)));
          if (offer.discount.type === 'fixed_amount') return Math.max(0,price-Math.round(Number(offer.discount.value)*100));
          return price;
        } catch {}
      }
      return undefined;
    }
    async perform(options) {
      let cart = options.cart?.item_count !== undefined ? options.cart : await this.get();
      const mains = new Set(cart.items.filter(item=>item.properties?._careiz_upsell !== 'true').map(item=>String(item.product_id)));
      const orphaned = cart.items.filter(item=>item.properties?._careiz_upsell === 'true' && !mains.has(String(item.properties?._careiz_main_product)));
      const oldMarketing = this.codes(cart).filter(code=>!internal(code));
      const code = options.marketingCode !== undefined ? options.marketingCode : this.marketing(cart);
      const remaining = cart.items.filter(item=>!orphaned.some(orphan=>orphan.key === item.key));
      const native = unique(remaining.filter(item=>item.properties?._careiz_upsell === 'true').map(item=>item.properties?._careiz_discount_code));
      const desired = unique([code,...native]);
      if (desired.length > 5) throw new Error(config.labels.tooManyCodes);
      const attributes = {_careiz_applied_codes:JSON.stringify(native),_careiz_marketing_code:code || ''};
      const previousCode = this.marketing(cart);
      const changed = orphaned.length > 0 || !same(this.codes(cart),desired) || (cart.attributes?._careiz_applied_codes || '[]') !== attributes._careiz_applied_codes || (cart.attributes?._careiz_marketing_code || '') !== (code || '') || options.marketingCode !== undefined;
      if (changed) cart = await this.update({updates:Object.fromEntries(orphaned.map(item=>[item.key,0])),discount:desired.join(','),attributes});
      const unpriced = cart.items.filter(item=> {
        const expected = this.expected(item,cart);
        return item.properties?._careiz_upsell === 'true' && Number.isFinite(expected) && Math.round(Number(item.final_line_price ?? item.line_price)/item.quantity) > expected + 1;
      });
      if (options.marketingCode !== undefined && code && (!this.accepted(cart,code) || unpriced.length)) {
        const rollback = await this.update({discount:unique([previousCode,...native]).join(','),attributes:{_careiz_applied_codes:JSON.stringify(native),_careiz_marketing_code:previousCode}});
        this.currentCode = previousCode;this.render(rollback);this.paint();
        throw new Error(unpriced.length ? config.labels.incompatibleCode : config.labels.invalidCode);
      }
      if (unpriced.length) {
        const kept = cart.items.filter(item=>!unpriced.some(line=>line.key === item.key));
        const keptCodes = unique(kept.filter(item=>item.properties?._careiz_upsell === 'true').map(item=>item.properties?._careiz_discount_code));
        cart = await this.update({updates:Object.fromEntries(unpriced.map(item=>[item.key,0])),discount:unique([code,...keptCodes]).join(','),attributes:{_careiz_applied_codes:JSON.stringify(keptCodes),_careiz_marketing_code:code || ''}});
      }
      this.currentCode = code || '';
      if (unpriced.length) this.message = config.labels.priceChanged;
      else if (orphaned.length) this.message = config.labels.orphanRemoved;
      else if (oldMarketing.length > 1) this.message = config.labels.oneCode;
      else if (code && cart.item_count > 0 && !this.accepted(cart,code)) this.message = config.labels.notApplicable || config.labels.invalidCode;
      else this.message = ''; 
      if ((changed || unpriced.length) && (!options.checkout || orphaned.length || unpriced.length)) {
        this.render(cart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'careiz-cart-policy',cartData:cart});
      }
      this.paint();
      return {cart,removed:orphaned.length+unpriced.length};
    }
    render(cart) {
      for (const [id,selector] of [['main-cart-items','.js-contents'],['main-cart-footer','.js-contents']]) {
        const host = document.getElementById(id);
        const html = cart.sections?.[host?.dataset.id || id];
        if (!host || !html) continue;
        const source = new DOMParser().parseFromString(html,'text/html').querySelector(selector);
        const target = host.querySelector(selector);
        if (source && target) target.innerHTML = source.innerHTML;
        host.classList.toggle('is-empty',cart.item_count === 0);
        host.closest('cart-items')?.classList.toggle('is-empty',cart.item_count === 0);
      }
      const drawer = document.querySelector('cart-drawer');
      const html = cart.sections?.['cart-drawer'];
      if (drawer && html) {
        drawer.updateContents?.(html);
      }
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleHTML = cart.sections?.['cart-icon-bubble'];
      if (bubble && bubbleHTML) {
        const source = new DOMParser().parseFromString(bubbleHTML,'text/html').querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }
    }
    paint() {
      document.querySelectorAll('[data-careiz-cart-notice]').forEach(element=>{element.textContent = this.message;element.hidden = !this.message;});
      document.querySelectorAll('careiz-coupon').forEach(element=>element.paint?.());
    }
    accepted(cart,code) {
      return (cart.discount_codes || []).some(discount=>upper(discount.code) === upper(code) && discount.applicable !== false) || this.allocations(cart).some(discount=>discount.type === 'discount_code' && upper(discount.code || discount.title) === upper(code));
    }
    async applyCode(code) {
      if (code && (code.length > 64 || /[,\r\n]/.test(code) || internal(code))) throw new Error(config.labels.invalidCode);
      await this.normalize({marketingCode:code});
      this.message = code ? config.labels.codeApplied : config.labels.codeRemoved;
      this.paint();
    }
  }
  const policy = new CartPolicy();window.CareizCart = policy;
  document.addEventListener('click',async event=> {
    const control = event.target.closest('cart-remove-button');
    if (!control || !control.dataset.index) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (control.getAttribute('aria-busy') === 'true') return;
    control.setAttribute('aria-busy','true');
    try {await policy.removeLine(control.dataset.index);}
    catch(error) {policy.message = error.message;policy.paint();}
    finally {control.removeAttribute('aria-busy');}
  },true);
  if (!customElements.get('careiz-coupon')) customElements.define('careiz-coupon',class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');this.status = this.querySelector('[data-code-status]');
      this.addEventListener('click',event=> {
        if (event.target.closest('[data-code-apply]')) this.apply(this.input.value.trim());
        if (event.target.closest('[data-code-remove]')) this.apply('');
      });
      this.input.addEventListener('keydown',event=>{if (event.key === 'Enter') {event.preventDefault();this.apply(this.input.value.trim());}});
      this.paint();
    }
    paint() {
      if (document.activeElement !== this.input) this.input.value = policy.currentCode;
      this.querySelector('[data-code-current]').textContent = policy.currentCode ? ` · ${policy.currentCode}` : '';
      this.querySelector('[data-code-remove]').hidden = !policy.currentCode;
    }
    async apply(code) {
      if (this.busy) return;this.busy = true;
      this.querySelectorAll('button,input').forEach(element=>element.disabled = true);
      try {await policy.applyCode(code);this.status.textContent = policy.message;}
      catch(error) {policy.message = error.message;policy.paint();this.status.textContent = error.message;}
      finally {this.busy = false;this.querySelectorAll('button,input').forEach(element=>element.disabled = false);this.paint();}
    }
  });
  document.addEventListener('submit',async event=> {
    const form = event.target;
    if (event.submitter?.name !== 'checkout') return;
    if (policy.allowedForms.has(form)) {policy.allowedForms.delete(form);return;}
    if (!new URL(form.action,location.href).pathname.replace(/\/$/,'').endsWith('/cart')) return;
    event.preventDefault();
    const submitter = event.submitter;
    try {
      const result = await policy.normalize({checkout:true});
      if (result.removed) return;
      policy.allowedForms.add(form);form.requestSubmit(submitter);
    } catch(error) {policy.message = error.message;policy.paint();}
  },true);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate,event=> {
    if (event.source === 'careiz-cart-policy') return;
    policy.normalize().catch(error=>{policy.message = error.message;policy.paint();});
  });
  policy.normalize().catch(error=>{policy.message = error.message;policy.paint();});
})();
